import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet } from "react-router-dom";
import { FileBarChart, LayoutDashboard, LogOut, Radio, ScanLine, Users, Wallet, Wifi, WifiOff } from "lucide-react";
import { useAuth } from "../auth";

const TABS = [
    { to: "/control", icon: LayoutDashboard, label: "Board", end: true, roles: ["admin", "manager", "staff"], testId: "tab-dashboard" },
    { to: "/control/scan", icon: ScanLine, label: "Scan", roles: ["admin", "manager", "staff"], testId: "tab-scan" },
    { to: "/control/live", icon: Radio, label: "Live", roles: ["admin", "manager", "staff"], testId: "tab-live" },
    { to: "/control/guests", icon: Users, label: "Guests", roles: ["admin", "manager", "staff"], testId: "tab-guests" },
    { to: "/control/finance", icon: Wallet, label: "Finance", roles: ["admin", "manager"], testId: "tab-finance" },
    { to: "/control/reports", icon: FileBarChart, label: "Reports", roles: ["admin", "manager"], testId: "tab-reports" },
];

export const ControlLayout = () => {
    const { user, logout, loading } = useAuth();
    const [online, setOnline] = useState(navigator.onLine);

    useEffect(() => {
        const on = () => setOnline(true);
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-lakou-void flex flex-col items-center justify-center gap-3" data-testid="control-loading">
                <div className="w-16 h-16 rounded-2xl border-2 border-lakou-gold flex items-center justify-center font-display font-black text-2xl text-lakou-gold">LK</div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-lakou-sand">LAKOU CONTROL</p>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (!["admin", "manager", "staff"].includes(user.role)) return <Navigate to="/" replace />;

    const tabs = TABS.filter((t) => t.roles.includes(user.role));

    return (
        <div className="min-h-screen bg-lakou-void text-lakou-cream" data-testid="control-app">
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-lakou-coal/85 border-b border-lakou-gold/15 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl border border-lakou-gold/50 flex items-center justify-center font-display font-black text-sm text-lakou-gold">LK</div>
                    <div>
                        <div className="font-display font-black text-sm tracking-[0.2em]" data-testid="control-brand">LAKOU CONTROL</div>
                        <div className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-lakou-sand">{user.email} · {user.role === "staff" ? "scanner" : user.role}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span data-testid="control-online-status"
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.15em] border ${online ? "border-emerald-500/40 text-emerald-400" : "border-red-500/50 text-red-400"}`}>
                        {online ? <Wifi size={11} /> : <WifiOff size={11} />} {online ? "Online" : "Offline"}
                    </span>
                    <button onClick={logout} data-testid="control-logout" aria-label="Log out"
                        className="p-2 rounded-full border border-lakou-gold/20 text-lakou-grey hover:text-lakou-gold transition-colors">
                        <LogOut size={15} />
                    </button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 pt-6 pb-28">
                <Outlet />
            </main>

            <nav className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-lakou-coal/90 border-t border-lakou-gold/15" data-testid="control-nav">
                <div className="max-w-5xl mx-auto grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
                    {tabs.map((t) => (
                        <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testId}
                            className={({ isActive }) => `flex flex-col items-center gap-1 py-3 transition-colors ${isActive ? "text-lakou-gold" : "text-lakou-sand hover:text-lakou-cream"}`}>
                            <t.icon size={19} />
                            <span className="font-mono2 text-[8px] uppercase tracking-[0.15em]">{t.label}</span>
                        </NavLink>
                    ))}
                </div>
            </nav>
        </div>
    );
};
